import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { FileText, FileCheck, DollarSign, Calendar, Download, CheckCircle } from "lucide-react";
import i18n from "@/i18n";

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function Reportes() {
  const { t } = useTranslation();
  const [fechaInicio, setFechaInicio] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
  
  const [reportData, setReportData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [reportRes, chartRes] = await Promise.all([
        axios.get(`${API}/stats/reports?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`),
        axios.get(`${API}/stats/chart-data`)
      ]);
      setReportData(reportRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      toast.error(t('reports.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [fechaInicio, fechaFin]);

  const handleExportCSV = () => {
    if (!reportData) return;
    
    let csv = t('reports.csvHeaders') + "\n";
    
    reportData.facturas.forEach(f => {
      csv += `${t('reports.csvInvoice')},${f.numero},${f.fecha},"${f.cliente_nombre}",${f.total},${f.estado}\n`;
    });
    
    reportData.documentos.forEach(d => {
      csv += `${t('reports.csvSupportDoc')},${d.numero},${d.fecha},"${d.proveedor_nombre}",${d.valor_total},${d.estado}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Reporte_TransFactura_${fechaInicio}_${fechaFin}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success(t('reports.exportSuccess'));
  };

  return (
    <div data-testid="reportes-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('reports.title')}</h1>
          <p className="text-slate-500 mt-1">{t('reports.subtitle')}</p>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="fecha_inicio" className="text-sm text-slate-600">{t('reports.startDate')}</Label>
              <Input id="fecha_inicio" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="w-[160px]" />
            </div>
            <div>
              <Label htmlFor="fecha_fin" className="text-sm text-slate-600">{t('reports.endDate')}</Label>
              <Input id="fecha_fin" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="w-[160px]" />
            </div>
            <Button onClick={fetchReport} className="bg-[#0f2a4a] hover:bg-[#1e3a8a]">
              <Calendar className="w-4 h-4 mr-2" />{t('reports.update')}
            </Button>
            <Button variant="outline" onClick={handleExportCSV} disabled={!reportData}>
              <Download className="w-4 h-4 mr-2" />{t('reports.exportCsv')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{t('reports.totalBilled')}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{formatCurrency(reportData.resumen.total_facturas)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-100 text-blue-600"><DollarSign className="w-5 h-5" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{t('reports.totalSupportDocs')}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{formatCurrency(reportData.resumen.total_documentos)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-100 text-amber-600"><DollarSign className="w-5 h-5" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{t('reports.invoices')}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{reportData.resumen.cantidad_facturas}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-100 text-green-600"><FileText className="w-5 h-5" /></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 font-medium">{t('reports.supportDocs')}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{reportData.resumen.cantidad_documentos}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-100 text-purple-600"><FileCheck className="w-5 h-5" /></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Combined Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">{t('reports.monthlyTrend')}</CardTitle>
              <p className="text-sm text-slate-500">{t('reports.monthlyTrendSubtitle')}</p>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mes" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                    <Legend />
                    <Line type="monotone" dataKey="facturas" stroke="#0f2a4a" strokeWidth={2} dot={{ fill: '#0f2a4a', strokeWidth: 2 }} name={t('reports.invoices')} />
                    <Line type="monotone" dataKey="documentos" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', strokeWidth: 2 }} name={t('reports.supportDocs')} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Accountant Summary */}
          <Card className="border-slate-200 border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-green-100 text-green-600"><CheckCircle className="w-6 h-6" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Barlow'] mb-2">{t('reports.prepareForAccountant')}</h3>
                  <p className="text-slate-700">
                    {t('reports.accountantSummary', {
                      invoiceCount: reportData.resumen.cantidad_facturas,
                      invoiceTotal: formatCurrency(reportData.resumen.total_facturas),
                      docCount: reportData.resumen.cantidad_documentos,
                      docTotal: formatCurrency(reportData.resumen.total_documentos)
                    })}
                    {' '}
                    <span className="text-green-600 font-medium">{t('reports.readyForReview')}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Combined Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">{t('reports.periodDetail')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-slate-600">{t('reports.type')}</TableHead>
                      <TableHead className="font-semibold text-slate-600">{t('reports.number')}</TableHead>
                      <TableHead className="font-semibold text-slate-600">{t('common.date')}</TableHead>
                      <TableHead className="font-semibold text-slate-600">{t('reports.counterpart')}</TableHead>
                      <TableHead className="font-semibold text-slate-600 text-right">{t('supportDocs.value')}</TableHead>
                      <TableHead className="font-semibold text-slate-600">{t('common.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...reportData.facturas.map(f => ({...f, tipo: 'factura'})), 
                      ...reportData.documentos.map(d => ({...d, tipo: 'documento'}))]
                      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                      .map((item, index) => (
                        <TableRow key={`${item.tipo}-${index}`} className="hover:bg-slate-50/50">
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${
                              item.tipo === 'factura' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {item.tipo === 'factura' ? (
                                <><FileText className="w-3 h-3 mr-1" />{t('reports.invoice')}</>
                              ) : (
                                <><FileCheck className="w-3 h-3 mr-1" />{t('reports.supportDoc')}</>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{item.numero}</TableCell>
                          <TableCell className="text-slate-600">{formatDate(item.fecha)}</TableCell>
                          <TableCell className="text-slate-900">{item.tipo === 'factura' ? item.cliente_nombre : item.proveedor_nombre}</TableCell>
                          <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(item.tipo === 'factura' ? item.total : item.valor_total)}</TableCell>
                          <TableCell>
                            <span className={`inline-flex px-2.5 py-1 rounded text-xs font-semibold ${
                              item.estado === 'Aceptada' ? 'bg-green-100 text-green-700' :
                              item.estado === 'Pendiente' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {item.estado === 'Aceptada' ? t('common.accepted') : item.estado === 'Pendiente' ? t('common.pending') : t('common.rejected')}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    {(reportData.facturas.length + reportData.documentos.length) === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">{t('reports.noDocsInPeriod')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
